import { WizardTextField, type WizardTextFieldProps } from "@ui/components/WizardTextField";
import { findCurrency, subunitFactor } from "@ui/lib/locale-data";
import type { TextFieldProps } from "@ui/primitives/TextField";
import type { ReactNode } from "react";

export interface MoneyFieldProps extends Omit<TextFieldProps, "type" | "children"> {
  /** ISO currency code (e.g. `"USD"`) — drives the symbol and decimal places. */
  currency: string;
  /** Amount in major units, as a string (e.g. `"29.00"`). Empty string when unset. */
  value: string;
  onChange: (value: string) => void;
  label: string;
  isLabelHidden?: boolean;
  placeholder?: string;
  color?: WizardTextFieldProps["color"];
  size?: WizardTextFieldProps["size"];
  /** Validation message rendered below the field when `isInvalid`. */
  errorMessage?: ReactNode;
  /** Calm "validated clean" message + green ring. Suppressed while `isInvalid`. */
  successMessage?: ReactNode;
}

/**
 * Currency-aware money input: a thin wrapper over `WizardTextField` (pill, focus/validation
 * rings, per-`color` theme) that leads with the currency symbol (`prefix`) and constrains typing
 * to a valid decimal amount — two fraction digits for standard currencies, none for zero-decimal
 * ones (JPY). The value is the major-unit amount as a string; callers convert to minor units
 * (`subunitFactor`) before persisting.
 */
export function MoneyField({ currency, value, onChange, ...props }: MoneyFieldProps) {
  const symbol = findCurrency(currency)?.symbol ?? currency;
  const allowDecimals = subunitFactor(currency) !== 1;

  return (
    <WizardTextField
      {...props}
      inputMode={allowDecimals ? "decimal" : "numeric"}
      onChange={(raw) => onChange(sanitizeAmount(raw, allowDecimals))}
      prefix={symbol}
      value={value}
    />
  );
}

/** Keep only digits and, when allowed, a single decimal point with up to two fraction digits. */
function sanitizeAmount(raw: string, allowDecimals: boolean): string {
  const digitsAndDots = raw.replace(/[^\d.]/g, "");
  if (!allowDecimals) return digitsAndDots.replace(/\./g, "");

  const firstDot = digitsAndDots.indexOf(".");
  if (firstDot === -1) return digitsAndDots;

  const intPart = digitsAndDots.slice(0, firstDot);
  const fractionPart = digitsAndDots
    .slice(firstDot + 1)
    .replace(/\./g, "")
    .slice(0, 2);
  return `${intPart}.${fractionPart}`;
}
